import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "select",
    "display",
    "dropdown",
    "selectedText",
    "arrow",
    "option",
  ];

  static values = {
    selected: String,
    placeholder: String,
    disabled: Boolean,
    required: Boolean,
  };

  connect() {
    this.isOpen = false;
    this.selectedIndex = this.getSelectedIndex();
    this.setupEventListeners();
  }

  disconnect() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    this.element.addEventListener("keydown", this.handleKeydown.bind(this));

    document.addEventListener("click", this.handleClickOutside.bind(this));
  }

  removeEventListeners() {
    document.removeEventListener("click", this.handleClickOutside.bind(this));
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  handleKeydown(event) {
    if (this.disabledValue) return;

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        this.toggle();
        break;
      case "Escape":
        this.close();
        break;
      case "ArrowDown":
        event.preventDefault();
        this.navigateDown();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.navigateUp();
        break;
      case "Tab":
        this.close();
        break;
    }
  }

  toggle(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.disabledValue) return;

    this.isOpen ? this.close() : this.open();
  }

  selectOption(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const option = event
      ? event.currentTarget
      : this.optionTargets[this.selectedIndex];
    if (!option) return;

    const value = option.dataset.value;
    const text = option.textContent.trim();

    this.updateSelection(value, text, option);
    this.close();
    this.dispatchChangeEvent();
  }

  navigateDown() {
    if (!this.isOpen) {
      this.open();
      return;
    }

    this.selectedIndex = Math.min(
      this.selectedIndex + 1,
      this.optionTargets.length - 1
    );
    this.updateVisualSelection();
  }

  navigateUp() {
    if (!this.isOpen) {
      this.open();
      return;
    }

    this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
    this.updateVisualSelection();
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.dropdownTarget.classList.remove("hidden");
    this.dropdownTarget.classList.add("block");
    this.arrowTarget.classList.remove("selectize-arrow-down");
    this.arrowTarget.classList.add("selectize-arrow-up");

    this.displayTarget.setAttribute("aria-expanded", "true");
    this.dropdownTarget.setAttribute("aria-hidden", "false");

    if (this.selectedIndex === -1) {
      this.selectedIndex = 0;
    }

    this.updateVisualSelection();
    this.dispatchEvent("opened");
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.dropdownTarget.classList.remove("block");
    this.dropdownTarget.classList.add("hidden");
    this.arrowTarget.classList.remove("selectize-arrow-up");
    this.arrowTarget.classList.add("selectize-arrow-down");

    this.displayTarget.setAttribute("aria-expanded", "false");
    this.dropdownTarget.setAttribute("aria-hidden", "true");

    this.dispatchEvent("closed");
  }

  updateSelection(value, text, optionElement) {
    this.selectTarget.value = value;
    this.selectedValue = value;

    this.selectedTextTarget.textContent = text;

    this.optionTargets.forEach((opt) => {
      opt.classList.remove(
        "bg-[var(--dd-item-selected-background)]",
        "text-[var(--dd-item-selected-color)]",
        "font-[var(--dd-item-selected-font-weight)]",
        "bg-blue-50",
        "text-blue-700",
        "font-medium"
      );
    });

    if (optionElement) {
      optionElement.classList.add(
        "bg-[var(--dd-item-selected-background)]",
        "text-[var(--dd-item-selected-color)]",
        "font-[var(--dd-item-selected-font-weight)]",
        "bg-blue-50",
        "text-blue-700",
        "font-medium"
      );
    }

    this.selectedIndex = this.optionTargets.indexOf(optionElement);
  }

  updateVisualSelection() {
    this.optionTargets.forEach((option, index) => {
      if (index === this.selectedIndex) {
        option.classList.add(
          "bg-[var(--dd-item-hover-background)]",
          "bg-gray-100"
        );
        option.scrollIntoView({ block: "nearest" });
      } else {
        option.classList.remove(
          "bg-[var(--dd-item-hover-background)]",
          "bg-gray-100"
        );
      }
    });
  }

  getSelectedIndex() {
    const selectedValue = this.selectedValue || this.selectTarget.value;
    return this.optionTargets.findIndex(
      (option) => option.dataset.value === selectedValue
    );
  }

  dispatchChangeEvent() {
    const changeEvent = new CustomEvent("select:change", {
      bubbles: true,
      detail: {
        value: this.selectedValue,
        text: this.selectedTextTarget.textContent,
        element: this.element,
      },
    });
    this.element.dispatchEvent(changeEvent);

    const nativeChangeEvent = new Event("change", { bubbles: true });
    this.selectTarget.dispatchEvent(nativeChangeEvent);
  }

  dispatchEvent(name) {
    const event = new CustomEvent(`select:${name}`, {
      bubbles: true,
      detail: { element: this.element },
    });
    this.element.dispatchEvent(event);
  }

  setValue(value) {
    const option = this.optionTargets.find(
      (opt) => opt.dataset.value === value
    );
    if (option) {
      this.selectOption(null);
      this.updateSelection(value, option.textContent.trim(), option);
    }
  }

  getValue() {
    return this.selectedValue;
  }

  enable() {
    this.disabledValue = false;
    this.displayTarget.removeAttribute("data-disabled");
    this.displayTarget.removeAttribute("aria-disabled");
  }

  disable() {
    this.disabledValue = true;
    this.displayTarget.setAttribute("data-disabled", "true");
    this.displayTarget.setAttribute("aria-disabled", "true");
    this.close();
  }
}
